import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { MaintenanceModeProvider } from './contexts/MaintenanceModeContext';
import { AuthProvider } from './context/AuthContext';
import { captureUtmParams } from '@/lib/utm';
import { trackPageView } from '@/lib/analytics';
import { LandingPage } from './pages/LandingPage';
import { EnterprisePage } from './pages/EnterprisePage';
import { PricingPage } from './pages/PricingPage';
import { SuccessPage } from './pages/SuccessPage';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { CookiePolicy } from './pages/CookiePolicy';
import { AdminPanel } from './pages/AdminPanel';
import { BrandingDemo } from './pages/BrandingDemo';
import { LinkedInCallback } from './pages/LinkedInCallback';
import { GoogleCallback } from './pages/GoogleCallback';
import { AccountPage } from './pages/AccountPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutCompletePage } from './pages/CheckoutCompletePage';
import { ForSalesPage } from './pages/ForSalesPage';
import { ForJobSeekersPage } from './pages/ForJobSeekersPage';
import { ForRecruitersPage } from './pages/ForRecruitersPage';
import { ChatWidget } from './components/ChatWidgetStreaming';
import { CookieConsent } from './components/CookieConsent';
import logoImage from '@/assets/xrepl-logo.png';

/** Track SPA page views on route changes */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

const GOOGLE_CLIENT_ID = '71406201363-hohot5f8q570o8sehmrf1l86vgqc0sui.apps.googleusercontent.com';

export default function App() {
  useEffect(() => {
    // Set page title
    document.title = 'xReplAI - Home';
    
    // Set favicon to logo
    let favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = logoImage;
    
    // Initialize dark mode from localStorage or system preference
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add('dark');
    }

    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    // Capture UTM parameters and referral codes from the landing URL
    captureUtmParams();

    // Eagerly load Google Identity Services script on app initialization
    // This eliminates the delay when user clicks "Continue with Google"
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        // Pre-initialize Google Identity Services (callback will be set when user clicks sign-in)
        if (window.google && window.google.accounts) {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: () => {}, // Placeholder callback, will be overridden in AuthModal
          });
        }
      };
      document.head.appendChild(script);
    }
  }, []);

  return (
    <AuthProvider>
      <MaintenanceModeProvider>
        <Router>
          <PageViewTracker />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/branding-demo" element={<BrandingDemo />} />
            <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/complete" element={<CheckoutCompletePage />} />
            <Route path="/for-sales" element={<ForSalesPage />} />
            <Route path="/for-jobseekers" element={<ForJobSeekersPage />} />
            <Route path="/for-recruiters" element={<ForRecruitersPage />} />
          </Routes>
          <ChatWidget />
          <CookieConsent />
        </Router>
      </MaintenanceModeProvider>
    </AuthProvider>
  );
}
